// /get-started — the destination of the /join-us "Get started" CTA.
//
// A two-step wizard rendered INSIDE the logged-out public shell (left nav +
// top bar stay put), styled like /auth/signup?journey=join-network:
//   1. About you — collected first, buffered client-side (no account yet).
//   2. Create your account — the SignupForm.
// After the account is created, /onboarding/j/about drains the buffer and
// finishes onboarding, so the visitor never re-answers "About you". The
// journey stepper is shown About-you-first via the stepIds override; the
// wordmark is dropped because the shell's top bar carries the brand.

import { useEffect, useRef, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslations } from 'use-intl';
import { Card, Checkbox, Container, Stack } from '@leanin/ui';
import { SignupForm } from '@/components/auth/SignupForm';
import { EntryJourneyStepper, type JourneyStepId } from '@/components/entry-journeys';
import { AboutYouForm } from '@/components/onboarding/AboutYouForm';
import {
  setPendingProfileMarketing,
  writePendingProfile,
} from '@/components/onboarding/pending-profile';
import { redirectIfSignedIn } from '@/lib/use-session';
import { writeEntryJourney } from '@/lib/entry-journey';
import { trackOnboardingStepCompleted, trackOnboardingStepViewed } from '@/lib/analytics/events';
import { useTrackOnce } from '@/lib/analytics/use-track-once';
import { getStartedStepParams } from '@/lib/analytics/onboarding-steps';

const JOURNEY = 'join-network' as const;
// About you first, account second — the reverse of the shared JOIN_STEPS
// order, which still drives the account-first ad-landing signup.
const STEP_IDS: readonly JourneyStepId[] = ['about-you', 'sign-up'];

export const Route = createFileRoute('/_public/get-started')({
  // Signed-in visitors have no signup to do — send them to the feed (same as
  // /auth/signup). The _public layout renders the anonymous shell around this
  // for signed-out visitors.
  beforeLoad: ({ context }) => redirectIfSignedIn(context),
  component: GetStartedPage,
});

function GetStartedPage() {
  const t = useTranslations('EntryJourneys');
  const [step, setStep] = useState<'about' | 'account'>('about');
  // Marketing opt-in lives on the Finish page (step 2). Its value is patched
  // into the buffered profile so /onboarding/j/about applies it post-signup.
  const [marketingOptIn, setMarketingOptIn] = useState(false);

  // Stash the journey before any OAuth round-trip so /auth/finishing routes
  // the new member into the join-network onboarding — mirrors /auth/signup.
  useEffect(() => {
    writeEntryJourney(JOURNEY);
  }, []);
  // The step-viewed event makes the about-first order measurable against the
  // ad landings' account-first funnel (see getStartedStepParams).
  useTrackOnce(() => trackOnboardingStepViewed(getStartedStepParams('about_you_preauth')));

  // The About form's submit button is never disabled (nothing is async on
  // step 1), so a same-tick double-submit would double-count the funnel
  // step. Fire the transition events once per mount; setStep is idempotent.
  const aboutTrackedRef = useRef(false);

  return (
    <Container max="lg" py="page" px="lg">
      {/* xl, not page: the stepper is this flow's heading — a page-tier gap
       * reads as a break between unrelated zones rather than heading→card. */}
      <Stack gap="xl" align="center">
        <EntryJourneyStepper
          journey={JOURNEY}
          current={step === 'about' ? 'about-you' : 'sign-up'}
          stepIds={STEP_IDS}
          labelOverrides={{ 'sign-up': t('steps.finish') }}
        />
        <Card style={{ width: '100%', maxWidth: 440 }}>
          {step === 'about' ? (
            <AboutYouForm
              submitLabel={t('onboarding.about.cta')}
              showMarketingOptIn={false}
              onSubmit={(values) => {
                // Buffer the answers; /onboarding/j/about submits them once
                // the account exists.
                writePendingProfile(values);
                if (!aboutTrackedRef.current) {
                  aboutTrackedRef.current = true;
                  trackOnboardingStepCompleted(getStartedStepParams('about_you_preauth'));
                  trackOnboardingStepViewed(getStartedStepParams('sign_up'));
                }
                setStep('account');
              }}
            />
          ) : (
            <SignupForm
              journey={JOURNEY}
              submitLabel={t('steps.finish')}
              extraConsent={
                <Checkbox
                  checked={marketingOptIn}
                  onCheckedChange={(v) => {
                    const next = v === true;
                    setMarketingOptIn(next);
                    setPendingProfileMarketing(next);
                  }}
                  label={
                    // Match the "Remember me" label exactly (14px label size
                    // at regular body weight) — same composed token shorthand.
                    <span
                      style={{
                        font: 'var(--type-body-weight) var(--type-label-size)/var(--type-label-line) var(--type-label-family)',
                      }}
                    >
                      {t('onboarding.about.marketingOptIn')}
                    </span>
                  }
                />
              }
            />
          )}
        </Card>
      </Stack>
    </Container>
  );
}
